import { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import cx from 'classnames';
import { currentNorwegianMonth } from 'src/utils/norwegianMonth';
import 'src/components/FilterTabs/filterTabs.css';
import './monthPeriodFilter.css';

export type MonthPeriod = 'all' | 'month';

const MIN_MONTH = '2000-01';

const shiftMonth = (month: string, delta: number): string => {
    const [year, number] = month.split('-').map(Number);
    const date = new Date(Date.UTC(year, number - 1 + delta, 1));
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(
        2,
        '0',
    )}`;
};

/**
 * Compact "All time | Month" pill shared by analytics and message lists. A selected month
 * shows as "‹ Sep 2026 ›"; the label opens the native month picker where the browser has one.
 */
export const MonthPeriodFilter = ({
    label,
    allTimeLabel,
    selectedMonthLabel,
    monthLabel,
    period,
    month,
    onPeriodChange,
    onMonthChange,
    marker,
    className,
    preview = false,
}: {
    label: string;
    allTimeLabel: ReactNode;
    selectedMonthLabel: ReactNode;
    monthLabel: string;
    period: MonthPeriod;
    month: string;
    onPeriodChange: (period: MonthPeriod) => void;
    onMonthChange: (month: string) => void;
    marker?: ReactNode;
    className?: string;
    /** Renders the look only, without the native month input, for layout measurement. */
    preview?: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const input = useRef<HTMLInputElement>(null);
    const latest = currentNorwegianMonth();
    const shortMonth = new Date(`${month}-15T12:00:00Z`).toLocaleDateString(
        i18n.language,
        { month: 'short', year: 'numeric' },
    );
    const openPicker = () => {
        try {
            input.current?.showPicker();
        } catch {
            input.current?.focus();
        }
    };
    return (
        <div
            className={cx('mundu-filter-tabs mundu-month-filter', className)}
            role="group"
            aria-label={label}
        >
            {marker && (
                <span className="mundu-month-filter-marker">{marker}</span>
            )}
            <button
                type="button"
                aria-pressed={period === 'all'}
                className={
                    period === 'all' ? 'mundu-filter-tab-active' : undefined
                }
                onClick={() => onPeriodChange('all')}
            >
                {allTimeLabel}
            </button>
            {period === 'month' ? (
                <span className="mundu-month-segment">
                    <button
                        type="button"
                        className="mundu-month-step"
                        aria-label={t('monthPeriod.previous')}
                        disabled={month <= MIN_MONTH}
                        onClick={() => onMonthChange(shiftMonth(month, -1))}
                    >
                        <ChevronLeft size={14} aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="mundu-month-label"
                        aria-label={`${t('monthPeriod.choose')}: ${shortMonth}`}
                        onClick={openPicker}
                    >
                        {shortMonth}
                    </button>
                    <button
                        type="button"
                        className="mundu-month-step"
                        aria-label={t('monthPeriod.next')}
                        disabled={month >= latest}
                        onClick={() => onMonthChange(shiftMonth(month, 1))}
                    >
                        <ChevronRight size={14} aria-hidden="true" />
                    </button>
                    {!preview && (
                        <input
                            ref={input}
                            aria-label={monthLabel}
                            className="mundu-month-native"
                            type="month"
                            value={month}
                            min={MIN_MONTH}
                            max={latest}
                            onChange={event => {
                                if (event.target.value)
                                    onMonthChange(event.target.value);
                            }}
                        />
                    )}
                </span>
            ) : (
                <button
                    type="button"
                    aria-pressed={false}
                    onClick={() => onPeriodChange('month')}
                >
                    {selectedMonthLabel}
                </button>
            )}
        </div>
    );
};
